import { useState, useRef } from 'react';
import { Button } from '../ui/Button';

interface ImageUploadProps {
  onUpload: (files: File[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  accept?: string;
  existingUrls?: string[];
  onRemove?: (url: string) => void;
  onReorder?: (newOrder: number[]) => void;
  onChange?: (previews: string[]) => void;
  onFilesChange?: (files: File[]) => void;
}

export function ImageUpload({
  onUpload,
  maxFiles = 5,
  maxSizeMB = 10,
  accept = 'image/*',
  existingUrls = [],
  onRemove,
  onReorder,
  onChange,
  onFilesChange,
}: ImageUploadProps) {
  const [previews, setPreviews] = useState<string[]>(existingUrls);
  const [previewFiles, setPreviewFiles] = useState<(File | null)[]>(existingUrls.map(() => null));
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image reordering state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  function handleFiles(files: FileList | null) {
    if (!files) return;

    setError(null);
    const fileArray = Array.from(files);

    // Validate file count
    if (previews.length + fileArray.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file sizes
    const oversized = fileArray.find((f) => f.size > maxSizeMB * 1024 * 1024);
    if (oversized) {
      setError(`File ${oversized.name} exceeds ${maxSizeMB}MB limit`);
      return;
    }

    // Validate file types
    const invalidType = fileArray.find((f) => !f.type.startsWith('image/'));
    if (invalidType) {
      setError(`File ${invalidType.name} is not an image`);
      return;
    }

    // Generate previews
    const newPreviews: string[] = [];
    const newPreviewFiles: File[] = [];
    fileArray.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        newPreviewFiles.push(file);
        if (newPreviews.length === fileArray.length) {
          const updated = [...previews, ...newPreviews];
          const updatedFiles = [...previewFiles, ...newPreviewFiles];
          setPreviews(updated);
          setPreviewFiles(updatedFiles);
          onChange?.(updated);
          onFilesChange?.(updatedFiles.filter((f): f is File => !!f));
        }
      };
      reader.readAsDataURL(file);
    });

    onUpload(fileArray);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  function handleRemove(index: number) {
    const url = previews[index];
    const newPreviews = previews.filter((_, i) => i !== index);
    const newPreviewFiles = previewFiles.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    setPreviewFiles(newPreviewFiles);
    onChange?.(newPreviews);
    onFilesChange?.(newPreviewFiles.filter((f): f is File => !!f));
    if (onRemove && existingUrls.includes(url)) {
      onRemove(url);
    }
  }

  // Image reordering handlers
  function handleImageDragStart(index: number, e: React.DragEvent) {
    e.stopPropagation(); // Prevent triggering file drop zone
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleImageDragOver(index: number, e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }

  function handleImageDrop(index: number, e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the previews array
    const newPreviews = [...previews];
    const newPreviewFiles = [...previewFiles];
    const [draggedItem] = newPreviews.splice(draggedIndex, 1);
    const [draggedFile] = newPreviewFiles.splice(draggedIndex, 1);
    newPreviews.splice(index, 0, draggedItem);
    newPreviewFiles.splice(index, 0, draggedFile);

    setPreviews(newPreviews);
    setPreviewFiles(newPreviewFiles);
    onChange?.(newPreviews);
    onFilesChange?.(newPreviewFiles.filter((f): f is File => !!f));

    // Calculate the new order as an array of original indices
    const newOrder = newPreviews.map(preview => previews.indexOf(preview));

    // Notify parent of the new order
    if (onReorder) {
      onReorder(newOrder);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }

  function handleImageDragEnd() {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragging
            ? 'border-blue-500 bg-blue-900/20'
            : 'border-slate-600 hover:border-slate-500 bg-slate-800'
        }`}
      >
        <div className="text-slate-400">
          <div className="text-4xl mb-2">📸</div>
          <p className="text-sm">
            Drag & drop images here, or click to select
          </p>
          <p className="text-xs mt-1">
            Max {maxFiles} files, {maxSizeMB}MB each
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-900 rounded p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Preview Grid */}
      {previews.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400">
            Drag images to reorder • First image will be the cover
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {previews.map((preview, index) => (
              <div
                key={preview}
                draggable
                onDragStart={(e) => handleImageDragStart(index, e)}
                onDragOver={(e) => handleImageDragOver(index, e)}
                onDrop={(e) => handleImageDrop(index, e)}
                onDragEnd={handleImageDragEnd}
                className={`relative group cursor-move transition-all ${
                  draggedIndex === index
                    ? 'opacity-50 scale-95'
                    : dragOverIndex === index
                    ? 'ring-2 ring-blue-500 scale-105'
                    : ''
                }`}
              >
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-slate-700 pointer-events-none"
                />
                {/* First image indicator */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Cover
                  </div>
                )}
                {/* Image number */}
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {previews.length > 0 && (
        <div className="text-xs text-slate-500">
          {previews.length} / {maxFiles} files
        </div>
      )}
    </div>
  );
}
